import { useMemo } from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { ProfileHeader } from "./ProfileHeader";
import { FormsTab } from "./tabs/FormsTab";
import { DocumentsTab } from "./tabs/DocumentsTab";
import CustomTabs from "../../../components/CustomTabs";
import { useCountries } from "../../../data/useCountries";
import { useEntityTypes } from "../../../data/useEntityTypes";
import {
  getDisplayName,
  getGenderLabel,
  getLeftMeta,
} from "../../../utils/profileMeta";
import { ApplicationsTab } from "./tabs/ApplicationsTab";
import { ScreeningTab } from "./tabs/ScreeningTab";
import { MonitoringTab } from "./tabs/MonitoringTab";
import { Profile } from "../../../types/Profile";
import { usePatchProfile } from "../../../data/usePatchProfile";
import { DetailsTab } from "./tabs/DetailsTab";
import { useAuth } from "../../../context/AuthContext";

export const ProfileTab = ({
  profile: profileOverride,
}: {
  profile?: Profile;
}) => {
  const outlet = useOutletContext<{ profile: Profile } | null>();
  const profile = profileOverride ?? outlet?.profile;

  const location = useLocation();
  const navigate = useNavigate();

  const { data: entityTypes } = useEntityTypes();
  const { data: countries } = useCountries();
  const { mutateAsync: updateProfile } = usePatchProfile();
  const { hasPermission } = useAuth();

  // Without the module's view permission the tab is disabled and its content
  // is never mounted, so its list API isn't called (a 403 there logs the user
  // out via the global interceptor).
  const canViewApplications = hasPermission("mandate.view");
  const canViewScreening = hasPermission("screening.view");
  const canViewMonitoring = hasPermission("monitoring.view");

  const isEntity = profile?._type === "entity";
  const reviewStatus = profile?.status ?? null;
  const registrationNumber = profile?.registration_number ?? "-";

  const displayName = getDisplayName(profile, isEntity);
  const genderLabel = getGenderLabel(profile?.gender);

  const countryName = useMemo(() => {
    const list = Array.isArray(countries) ? countries : countries?.data ?? [];
    return list.find((x: any) => x.id === profile?.country)?.name ?? "-";
  }, [countries, profile?.country]);

  const entityTypeName = useMemo(() => {
    const et = profile?.entity_type;
    // The API returns entity_type either as an object { id, name } or a bare id.
    if (et && typeof et === "object" && et.name) return et.name;
    const list = Array.isArray(entityTypes)
      ? entityTypes
      : entityTypes?.data ?? [];
    const id = typeof et === "number" ? et : et?.id;
    return list.find((x: any) => x.id === id)?.name ?? "-";
  }, [entityTypes, profile?.entity_type]);

  const leftMeta = getLeftMeta({
    isEntity,
    registrationNumber,
    entityTypeName,
    genderLabel,
    countryName,
  });

  const activeTabIndex = useMemo(() => {
    const tab = new URLSearchParams(location.search).get("tab");

    if (!tab) return 0;

    switch (tab) {
      case "details":
        return 0;
      case "applications":
        return canViewApplications ? 1 : 0;
      case "screening":
        return canViewScreening ? 2 : 0;
      case "monitoring":
        return canViewMonitoring ? 3 : 0;
      case "documents":
        return 4;
      case "forms":
        return 5;
      default:
        return canViewApplications ? 1 : 0;
    }
  }, [
    location.search,
    canViewApplications,
    canViewScreening,
    canViewMonitoring,
  ]);

  const handleTabChange = (index: number) => {
    const tabKey =
      index === 0
        ? "details"
        : index === 1
        ? "applications"
        : index === 2
        ? "screening"
        : index === 3
        ? "monitoring"
        : index === 4
        ? "documents"
        : "forms";

    navigate({ search: `?tab=${tabKey}` }, { replace: true });
  };

  if (!profile) return null;

  return (
    <>
      <ProfileHeader
        isEntity={isEntity}
        displayName={displayName}
        leftMeta={leftMeta}
        entityTypeName={entityTypeName}
        registrationNumber={registrationNumber}
        reviewStatus={reviewStatus}
        profileId={profile.id}
        profileDisplayName={displayName}
        owner={profile.owner}
        reviewer={profile.reviewer}
        profileReference={profile.reference}
        profileExternalReference={profile.external_reference}
        periodicReview={profile?.periodic_review}
        periodicReviewDate={profile?.periodic_review_date}
      />

      <CustomTabs
        variant="segmented"
        tabs={[
          {
            label: "Details",
            content: (
              <DetailsTab
                profile={profile}
                isEntity={isEntity}
                entityTypes={entityTypes}
                countries={countries}
                onSave={(payload) => updateProfile(payload)}
              />
            ),
          },
          {
            label: "Applications",
            content: canViewApplications ? (
              <ApplicationsTab profile={profile} />
            ) : null,
            isDisabled: !canViewApplications,
            title: canViewApplications
              ? undefined
              : "You don't have permission to view applications",
          },
          {
            label: "Screening",
            content: canViewScreening ? <ScreeningTab profile={profile} /> : null,
            isDisabled: !canViewScreening,
            title: canViewScreening
              ? undefined
              : "You don't have permission to view screenings",
          },
          {
            label: "Monitoring",
            content: canViewMonitoring ? (
              <MonitoringTab profile={profile} />
            ) : null,
            isDisabled: !canViewMonitoring,
            title: canViewMonitoring
              ? undefined
              : "You don't have permission to view monitoring records",
          },
          { label: "Documents", content: <DocumentsTab profile={profile} /> },
          { label: "Forms", content: <FormsTab profile={profile} /> },
        ]}
        activeTabIndex={activeTabIndex}
        onTabChange={handleTabChange}
      />
    </>
  );
};
